import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-normal ' +
    'transition-[color,box-shadow,border-color] disabled:pointer-events-none disabled:opacity-50 ' +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none " +
    'focus-visible:ring-0.75 focus-visible:border-ring focus-visible:ring-ring/50 ' +
    'aria-invalid:ring-danger/20 dark:aria-invalid:ring-danger/40 aria-invalid:border-danger',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        outline:
          'border border-input bg-background shadow-xs ' +
          'active:bg-accent/20 active:text-accent active:border-accent ' +
          'hover:text-accent/80 hover:border-accent/80',
        outline_primary:
          'border border-accent text-accent bg-background shadow-xs ' +
          'active:bg-accent-select-background active:text-accent-active active:border-accent-active ' +
          'hover:text-accent-hover hover:border-accent-hover',
        secondary: 'bg-active text-foreground shadow-xs hover:bg-input active:bg-muted-foreground',
        ghost: 'text-accent hover:bg-hover bg-transparent active:bg-active',
        link: 'text-primary underline-offset-4 font-normal hover:underline hover:text-accent-hover active:text-accent-active',
      },
      size: {
        default: 'h-8 px-4 py-2 has-[>svg]:px-3 min-w-16',
        sm: 'h-6 text-sm rounded-md gap-1.5 px-2 has-[>svg]:px-1.5',
        lg: 'h-10 min-w-20 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />
}
function LoadingButton({ children, loading, ...props }: React.ComponentProps<typeof Button> & { loading?: boolean }) {
  return (
    <Button {...props}>
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </Button>
  )
}
export { Button, buttonVariants, LoadingButton }
